"use client";

import React from "react";

import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

// Eager load critical components (NavBar and Auth pages)
import NavBar from "./Components/NavBar/NavBar";
import Login from "./Pages/Login/Login";
import SignUp from "./Pages/SignUp/SignUp";
import PasswordReset from "./Pages/ResetPassword/ResetPassword";
import PasswordUpdate from "./Pages/PasswordUpdate/PasswordUpdate";

// Lazy load non-critical page components
const LandingPage = lazy(() => import("./Pages/LandingPage/LandingPage"));
const DashboardLayout = lazy(() =>
  import("./Components/DashboardLayout/DashboardLayout")
);
const DashboardHome = lazy(() => import("./Pages/Dashboard/DashboardHome"));
const DeviceInventory = lazy(() =>
  import("./Pages/DeviceInventory/DeviceInventory")
);
const FamilyMembers = lazy(() => import("./Pages/FamilyMembers/FamilyMembers"));
const ProfilePage = lazy(() => import("./Pages/Profile/Profile"));
const DeviceDetails = lazy(() => import("./Pages/DeviceDetails/DeviceDetails"));
const Maintenance = lazy(() => import("./Pages/Maintenance/Maintenance"));
const HelpSupport = lazy(() => import("./Pages/HelpSupport/HelpSupport"));
const Analytics = lazy(() => import("./Pages/Analytics/Analytics"));
const Settings = lazy(() => import("./Pages/Settings/Settings"));
const Notifications = lazy(() => import("./Pages/Notifications/Notifications"));
const TestUserData = lazy(() => import("./Pages/TestUserData"));

// Lazy load component pages
const Add = lazy(() => import("./Components/Add/Add"));
const Partners = lazy(() => import("./Components/Partners/Partners"));
const WhyChooseUs = lazy(() => import("./Components/WhyChooseUs/WhyChooseUs"));
const Benefits = lazy(() => import("./Components/Benefits/Benefits"));
const Features = lazy(() => import("./Components/Features/Features"));
const Works = lazy(() => import("./Components/Works/Works"));
const Subscription = lazy(() =>
  import("./Components/Subscription/Subscription")
);
const TestimonialsPage = lazy(() =>
  import("./Components/Testimonial/Testimonial")
);
const Family = lazy(() => import("./Components/ContactUs/ContactUs"));
const TestAssignModal = lazy(() => import("./Pages/TestAssignModal"));

// Enhanced Loading component with different styles for different contexts
const LoadingSpinner = ({ minimal = false, dashboard = false }) => {
  if (minimal) {
    return (
      <div className="minimal-loading">
        <div className="minimal-spinner"></div>
      </div>
    );
  }

  if (dashboard) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p className="loading-text">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="loading-container">
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p className="loading-text">Loading...</p>
      </div>
    </div>
  );
};

// Error Boundary for lazy loading failures
class LazyLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Lazy loading error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-container">
          <h2>Something went wrong.</h2>
          <p>Please refresh the page and try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Component to conditionally render NavBar
function AppContent() {
  const location = useLocation();

  // Define pages where NavBar should not appear
  const noNavBarPages = [
    // Auth pages
    "/login",
    "/signup",
    "/forgotPassword",
    "/passwordUpdate",
    // Dashboard and user-specific pages (now handled by DashboardLayout)
    "/dashboard",
    "/device-inventory",
    "/family-members",
    "/maintenance",
    "/analytics",
    "/help-support",
    "/settings",
    "/profile",
    "/notifications",
  ];

  // Check if current path should hide NavBar (includes dynamic routes)
  const shouldHideNavBar =
    noNavBarPages.includes(location.pathname) ||
    location.pathname.startsWith("/device-details/");

  return (
    <LazyLoadErrorBoundary>
      {/* NavBar will not be present on specified pages */}
      {!shouldHideNavBar && <NavBar />}

      <Routes>
        {/* Default route - redirects to landing page */}
        <Route path="/" element={<Navigate to="/landing" replace />} />

        {/* Auth pages without NavBar - NO LAZY LOADING for better UX */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgotPassword" element={<PasswordReset />} />
        <Route path="/passwordUpdate" element={<PasswordUpdate />} />

        {/* Landing page route with lazy loading */}
        <Route
          path="/landing"
          element={
            <Suspense fallback={<LoadingSpinner minimal={true} />}>
              <LandingPage />
            </Suspense>
          }
        />

        {/* Dashboard routes with DashboardLayout and enhanced loading */}
        <Route
          path="/dashboard"
          element={
            <Suspense fallback={<LoadingSpinner dashboard={true} />}>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner minimal={true} />}>
                  <DashboardHome />
                </Suspense>
              </DashboardLayout>
            </Suspense>
          }
        />
        <Route
          path="/device-inventory"
          element={
            <Suspense fallback={<LoadingSpinner dashboard={true} />}>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner minimal={true} />}>
                  <DeviceInventory />
                </Suspense>
              </DashboardLayout>
            </Suspense>
          }
        />
        <Route
          path="/family-members"
          element={
            <Suspense fallback={<LoadingSpinner dashboard={true} />}>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner minimal={true} />}>
                  <FamilyMembers />
                </Suspense>
              </DashboardLayout>
            </Suspense>
          }
        />
        <Route
          path="/profile"
          element={
            <Suspense fallback={<LoadingSpinner dashboard={true} />}>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner minimal={true} />}>
                  <ProfilePage />
                </Suspense>
              </DashboardLayout>
            </Suspense>
          }
        />
        <Route
          path="/device-details/:deviceId"
          element={
            <Suspense fallback={<LoadingSpinner dashboard={true} />}>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner minimal={true} />}>
                  <DeviceDetails />
                </Suspense>
              </DashboardLayout>
            </Suspense>
          }
        />
        <Route
          path="/maintenance"
          element={
            <Suspense fallback={<LoadingSpinner dashboard={true} />}>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner minimal={true} />}>
                  <Maintenance />
                </Suspense>
              </DashboardLayout>
            </Suspense>
          }
        />
        <Route
          path="/test-userdata"
          element={
            <Suspense fallback={<LoadingSpinner dashboard={true} />}>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner minimal={true} />}>
                  <TestUserData />
                </Suspense>
              </DashboardLayout>
            </Suspense>
          }
        />
        <Route
          path="/analytics"
          element={
            <Suspense fallback={<LoadingSpinner dashboard={true} />}>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner minimal={true} />}>
                  <Analytics />
                </Suspense>
              </DashboardLayout>
            </Suspense>
          }
        />
        <Route
          path="/help-support"
          element={
            <Suspense fallback={<LoadingSpinner dashboard={true} />}>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner minimal={true} />}>
                  <HelpSupport />
                </Suspense>
              </DashboardLayout>
            </Suspense>
          }
        />
        <Route
          path="/settings"
          element={
            <Suspense fallback={<LoadingSpinner dashboard={true} />}>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner minimal={true} />}>
                  <Settings />
                </Suspense>
              </DashboardLayout>
            </Suspense>
          }
        />
        <Route
          path="/notifications"
          element={
            <Suspense fallback={<LoadingSpinner dashboard={true} />}>
              <DashboardLayout>
                <Suspense fallback={<LoadingSpinner minimal={true} />}>
                  <Notifications />
                </Suspense>
              </DashboardLayout>
            </Suspense>
          }
        />

        {/* Component pages with NavBar and lazy loading */}
        <Route
          path="/add"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Add />
            </Suspense>
          }
        />
        <Route
          path="/partners"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Partners />
            </Suspense>
          }
        />
        <Route
          path="/why-choose-us"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <WhyChooseUs />
            </Suspense>
          }
        />
        <Route
          path="/benefits"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Benefits />
            </Suspense>
          }
        />
        <Route
          path="/features"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Features />
            </Suspense>
          }
        />
        <Route
          path="/works"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Works />
            </Suspense>
          }
        />
        <Route
          path="/subscription"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Subscription />
            </Suspense>
          }
        />
        <Route
          path="/testimonials"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <TestimonialsPage />
            </Suspense>
          }
        />
        <Route
          path="/contact"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Family />
            </Suspense>
          }
        />

        {/* Test route for Assign Device Modal */}
        <Route
          path="/test-assign"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <TestAssignModal />
            </Suspense>
          }
        />

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/landing" replace />} />
      </Routes>
    </LazyLoadErrorBoundary>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
